import { useTranslation } from 'react-i18next';
import { useModeStore } from '@/store/mode-store';
import type { DashboardMode } from '@/persistence/config-db';

type ModeOption = {
  value: DashboardMode;
  labelKey: string;
  icon: JSX.Element;
};

const NORMAL_ICON = (
  <>
    <rect x="2" y="2" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
    <rect x="9" y="2" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
    <rect x="2" y="9" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
    <rect x="9" y="9" width="5" height="5" rx="1" strokeWidth="1.4" fill="none" />
  </>
);

const COMPARE_ICON = (
  <>
    <path
      d="M2 4h5v8H2zM9 4h5v8H9z"
      strokeWidth="1.5"
      fill="none"
      strokeLinejoin="round"
    />
    <path d="M7 8h2" strokeWidth="1.5" strokeLinecap="round" />
  </>
);

const MODES: ReadonlyArray<ModeOption> = [
  { value: 'normal', labelKey: 'mode.normal', icon: NORMAL_ICON },
  { value: 'compare', labelKey: 'mode.compare', icon: COMPARE_ICON },
];

/**
 * Segmented mode toggle rendered in the top bar (Phase 11 batch 3.3).
 * Normal vs Compare own separate widgets, layouts, and pinned filters.
 *
 * `disabled=true` greys the control out (used when no dataset is loaded).
 */
export function ModeToggle({
  disabled = false,
}: {
  disabled?: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const mode = useModeStore((s) => s.mode);
  const setMode = useModeStore((s) => s.setMode);

  return (
    <div className="flex gap-1.5" role="radiogroup" aria-label={t('mode.label')}>
      {MODES.map((opt) => {
        const active = mode === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            disabled={disabled}
            onClick={() => {
              if (!active) void setMode(opt.value);
            }}
            className="flex items-center gap-1.5 rounded-md disabled:opacity-50"
            style={{
              padding: '5px 10px',
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              border: `1.5px solid ${
                active ? 'var(--color-accent)' : 'var(--color-border)'
              }`,
              background: 'transparent',
              color: active ? 'var(--color-fg)' : 'var(--color-muted)',
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 16 16"
              fill="none"
              stroke={active ? 'var(--color-accent)' : 'var(--color-muted)'}
              aria-hidden
            >
              {opt.icon}
            </svg>
            {t(opt.labelKey)}
          </button>
        );
      })}
    </div>
  );
}
